// พื้นที่ของฉัน → ลงเวลา. The self-service half of clock-in: geolocation,
// the Bangkok day, and the pre-flight distance hint.
//
// Everything about a PUNCH (accessors, สาย/OT, status pill, payroll period) is
// already in `./clockin.js` and is shared with the HR report — this file adds
// only what the self-service page needs on top and deliberately does not
// restate any of it.

/* ── the Bangkok day ─────────────────────────────────────────────────────
 * Staff phones are on Asia/Bangkok, but a device with the wrong timezone must
 * still agree with the server about which shift is "today" — the proxy rejects
 * anything else. So the day is computed from the UTC epoch + 07:00, exactly as
 * functions/api/me/clockin/[id].js does it, not from local getters. */
const pad = (n) => String(n).padStart(2, '0')

export function bkkToday(now = Date.now()) {
  const d = new Date(now + 7 * 3600 * 1000)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

export const bkkYesterday = (now = Date.now()) => bkkToday(now - 24 * 3600 * 1000)

/* ── geolocation ─────────────────────────────────────────────────────────
 * The live Bubble logs show roughly 4 in 10 punches rejected for distance,
 * almost all of them the same person tapping again seconds later — a first fix
 * that came off wifi/cell triangulation, kilometres out, followed by a real GPS
 * fix once the radio settled. So: high accuracy on, no cached position, and the
 * page shows the accuracy radius it got, because a ±2 km fix is the thing the
 * user needs to see BEFORE they tap, not after they are refused. */
export const GEO_OPTS = { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }

// Thai for each of the three W3C error codes. A raw "User denied Geolocation"
// tells a receptionist nothing about what to do next.
const GEO_ERRORS = {
  1: 'ไม่ได้รับอนุญาตให้ใช้ตำแหน่ง — กรุณาเปิดสิทธิ์ตำแหน่ง (Location) ให้เว็บนี้ในตั้งค่าของเครื่อง แล้วลองใหม่',
  2: 'หาตำแหน่งไม่ได้ — กรุณาเปิด GPS/Location ของเครื่อง แล้วลองใหม่',
  3: 'หาตำแหน่งนานเกินไป — กรุณาออกไปที่โล่งหรือใกล้หน้าต่าง แล้วลองใหม่',
}

export function geoErrorMessage(err) {
  return GEO_ERRORS[err?.code] || 'หาตำแหน่งไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
}

// Promise wrapper. Rejects with an Error carrying a Thai `message` and the
// original `code`, so callers never have to know the W3C constants.
export function getPosition(opts = GEO_OPTS) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(Object.assign(new Error('อุปกรณ์นี้ไม่รองรับการระบุตำแหน่ง'), { code: 0 }))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({
        lat: p.coords.latitude,
        lon: p.coords.longitude,
        accuracy: Number(p.coords.accuracy) || 0,
        at: p.timestamp || Date.now(),
      }),
      (e) => reject(Object.assign(new Error(geoErrorMessage(e)), { code: e?.code })),
      opts,
    )
  })
}

// A fix older than this is not evidence of where you are standing now, so the
// page re-acquires rather than punching with it.
export const FIX_MAX_AGE_MS = 60 * 1000
export const isFixFresh = (fix, now = Date.now()) => !!fix && (now - Number(fix.at || 0)) < FIX_MAX_AGE_MS

// Above this the fix is too vague to say anything useful about a 1 km fence —
// it is shown as a warning, never as a block. The server decides.
export const POOR_ACCURACY_M = 200

/* ── distance ────────────────────────────────────────────────────────────
 * Haversine, metres. Purely a HINT so the user can see they are still on the
 * BTS before they tap. The real gate is Xano's `util.geo_distance` against
 * `$env.distance_clockin_limit`; this number is never sent and never enforced.
 * If the two ever disagree, the server is right. */
export const CLOCKIN_RADIUS_M = 1000 // mirrors distance_clockin_limit, for the hint only

export function distanceMeters(a, b) {
  if (!a || !b) return null
  const [lat1, lon1, lat2, lon2] = [a.lat, a.lon, b.lat, b.lon].map(Number)
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null
  const R = 6371008.8
  const rad = Math.PI / 180
  const dLat = (lat2 - lat1) * rad
  const dLon = (lon2 - lon1) * rad
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

export function fmtMeters(m) {
  if (m == null || !Number.isFinite(m)) return '—'
  return m < 1000 ? `${Math.round(m)} ม.` : `${(m / 1000).toFixed(1)} กม.`
}

// A Branch row's point, in the shape distanceMeters wants. Branches without a
// GPS point simply produce no hint (null) rather than a wrong one.
export function branchPoint(branch) {
  const d = branch?.gps?.data
  const lat = Number(d?.lat)
  const lon = Number(d?.lng)
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null
}

/* ── errors ──────────────────────────────────────────────────────────────
 * The proxies answer {error, detail, hint}; Xano answers {code, message}. The
 * api.js helpers throw `HTTP 422: {…json…}`, so the sentence the user needs is
 * inside the thrown message. Dig it back out — without this the page shows the
 * envelope and drops the only line that says what went wrong. */
export function pickMessage(j, status) {
  return j?.hint || j?.detail || j?.message || j?.error || `HTTP ${status}`
}

export function readableError(e, fallback = 'ลงเวลาไม่สำเร็จ') {
  const raw = String(e?.message || e || '')
  const m = raw.match(/\{[\s\S]*\}$/)
  if (m) {
    try { return pickMessage(JSON.parse(m[0]), '') } catch { /* fall through */ }
  }
  return raw || fallback
}
